// Styles specific to the vineyards table view
import "./VineyardsTable.scss";

// React Router hooks for navigation and URL query parameters
import { useNavigate, useSearchParams } from "react-router-dom";

// Styled-system Box component for layout
import { Box } from "../../components/styled-system/Box";

// Bootstrap components for pagination, spinner, and table
import Pagination from "react-bootstrap/Pagination";
import { Spinner } from "react-bootstrap";
import Table from "react-bootstrap/Table";

// React hooks and utilities
import React, { useEffect, useState } from "react";

// Types and helper components for sorting
import { SortOrder } from "../../components/table/table";
import { TableHeader } from "../../components/table/TableHeader";
import { AppSeo } from "../../components/app-seo/AppSeo";

// Components for search tips and options
import { SearchTips } from "../../components/search-tips/SearchTips";
import { SearchOptions } from "../../components/search-options/SearchOptions";

// API functions to fetch vineyard data based on various filters
import {
  getVineyards,
  getVineyardsByAge,
  getVineyardsByCountry,
  getVineyardsByVariety,
} from "../../api/old-vine-registry-api";

// Data models returned by the API
import { Vineyard, VineyardsResultPage } from "../../api/models";

// Route paths for navigation
import { Paths } from "../../paths";
// Custom hook to determine the current breakpoint (mobile, tablet, desktop)
import { useBreakpoint } from "../../hooks/useBreakpoint";

// Number of records per page
const pageSize = 500;

/**
 * Loading indicator component that shows a spinner when data is being fetched.
 * @param loading - whether the data fetch is in progress
 */
const Loading = (props: { loading: boolean }) => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    height="25px"
    mt="20px"
  >
    {props.loading && <Spinner animation="border" role="status" />}
  </Box>
);

/**
 * Table cell wrapper to vertically center content.
 * @param children - cell content
 */
const TD = ({ children }: { children: React.ReactNode }) => (
  <td>
    <Box height="100%" display="flex" alignItems="center">
      {children}
    </Box>
  </td>
);

/**
 * Custom hook to load vineyard data based on query parameters.
 * Manages pagination, sorting, and various filter scenarios.
 */
function useVineyardsLoader(queryOn: { text?: string; age?: string }) {
  const [vineyards, setVineyards] = useState<Vineyard[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);

  const [searchParams, setSearchParams] = useSearchParams();

  const currentParams = () => {
    let result: Record<string, string | null> = {};
    ["search", "country", "variety", "age"].forEach((key) => {
      if (searchParams.has(key) && searchParams.get(key)) {
        result[key] = searchParams.get(key);
      }
    });
    return result;
  };

  const loadData = async (
    queryOn: { text?: string; age?: string; country?: string; variety?: string },
    orderBy = "default",
    order: SortOrder = "asc",
    page = 0
  ) => {
    setLoading(true);
    let vineyardsResultPage: VineyardsResultPage;
    const vineyardQueryBase = { field: orderBy, order, pageNumber: page, pageSize };

    if (queryOn.age) {
      vineyardsResultPage = await getVineyardsByAge({
        ...vineyardQueryBase,
        searchValue: queryOn.age,
      });
    } else if (queryOn.country) {
      vineyardsResultPage = await getVineyardsByCountry({
        ...vineyardQueryBase,
        searchValue: queryOn.country,
      });
    } else if (queryOn.variety) {
      vineyardsResultPage = await getVineyardsByVariety({
        ...vineyardQueryBase,
        searchValue: queryOn.variety,
      });
    } else {
      vineyardsResultPage = await getVineyards({
        ...vineyardQueryBase,
        searchText: queryOn.text,
      });
    }

    setLoading(false);
    setVineyards(vineyardsResultPage.content || []);
    setFirstLoad(false);
    setTotalResults(vineyardsResultPage.totalElements);
    setTotalPages(vineyardsResultPage.totalPages);
    setCurrentPage(page);
  };

  const nextPage = (orderBy = "default", order: SortOrder = "asc") => {
    if (currentPage < totalPages - 1) {
      const next = currentPage + 1;
      setCurrentPage(next);
      setSearchParams({ ...currentParams(), page: `${next + 1}` });
      loadData(queryOn, orderBy, order, next);
    }
  };
  const prevPage = (orderBy = "default", order: SortOrder = "asc") => {
    if (currentPage > 0) {
      const prev = currentPage - 1;
      setCurrentPage(prev);
      setSearchParams({ ...currentParams(), page: `${prev + 1}` });
      loadData(queryOn, orderBy, order, prev);
    }
  };
  const lastPage = (orderBy = "default", order: SortOrder = "asc") => {
    const last = totalPages - 1;
    setCurrentPage(last);
    setSearchParams({ ...currentParams(), page: `${last + 1}` });
    loadData(queryOn, orderBy, order, last);
  };
  const firstPage = (orderBy = "default", order: SortOrder = "asc") => {
    setCurrentPage(0);
    setSearchParams({ ...currentParams(), page: "1" });
    loadData(queryOn, orderBy, order, 0);
  };

  useEffect(() => {
    if (firstLoad) {
      // Determine initial sort, order, and page from URL params
      const initialSort = searchParams.get("sort") || "default";
      const initialOrder = (searchParams.get("order") as SortOrder) || "asc";
      const initialPage = searchParams.has("page") ? Number(searchParams.get("page")) - 1 : 0;
      loadData(queryOn, initialSort, initialOrder, initialPage);
    }
  }, [queryOn, firstLoad, searchParams]);

  return {
    loading,
    data: vineyards,
    totalResults,
    totalPages,
    currentPage,
    loadData,
    firstPage,
    prevPage,
    nextPage,
    lastPage,
  };
}

/**
 * Main component rendering the vineyards table with search, filters, sorting, and pagination.
 */
export const VineyardsTable = () => {
  const navigate = useNavigate();
  const bp = useBreakpoint();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize sorting from URL
  const [sortByField, setSortByField] = useState<string>(
    () => searchParams.get("sort") || "default"
  );

  const [sortOrder, setSortOrder] = useState<SortOrder>(
    () => (searchParams.get("order") as SortOrder) || "asc"
  );

  const [searchText, setSearchText] = useState("");

  // Build filter object from URL parameters
  const queryOn = () => ({
    text: searchParams.get("search") || "",
    age: searchParams.get("age") || "",
    country: searchParams.get("country") || "",
    variety: searchParams.get("variety") || "",
  });

  // Hook for data loading and pagination
  const {
    loading,
    data: tableData,
    totalResults,
    totalPages,
    currentPage,
    loadData,
    nextPage,
    lastPage,
    prevPage,
    firstPage,
  } = useVineyardsLoader(queryOn());

  // Sync sorting and pagination state into URL, filtering out empty values
  useEffect(() => {
    // build only the non-empty filter params
    const filterParams: Record<string,string> = {};
    ["search", "country", "variety", "age"].forEach((key) => {
      const v = searchParams.get(key);
      if (v) filterParams[key] = v;
    });
  
    setSearchParams({
      ...filterParams,
      sort:  sortByField,
      order: sortOrder,
      page:  `${currentPage + 1}`,
    });
  }, [
    // now we only depend on these
    searchParams,
    sortByField,
    sortOrder,
    currentPage,
    setSearchParams,
  ]);

  /**
   * Handle sorting changes: if same field, toggle order; otherwise default to ascending
   */
  const reloadAfterSortOrderChange = (field: string, order: SortOrder) => {
    const newOrder = field === sortByField ? order : "asc";
    setSortByField(field);
    setSortOrder(newOrder);
    firstPage(field, newOrder);
  };
    /**
   * Trigger a new text-based search, reset to first page, and update URL params including sort/order.
   */
  const runTextQuery = () => {
    const params = {
      search: searchText,
      sort: sortByField,
      order: sortOrder,
      page: "1"
    };
    setSearchParams(params);
    loadData({ text: searchText }, sortByField, sortOrder, 0);
  };

  // Side effect to set document title on mount/unmount
  useEffect(() => {
    document.title = 'Old Vine Registry Results';
    return () => { document.title = 'Old Vine Registry'; };
  }, []);

  return (
    <><AppSeo
        title="Vineyard Search Results"
        keywords="old vines, vineyards, search, wine heritage, vintage wines"
    />
    <div className="content-panel">
      {/* Search result summary */}
      <span className="title-large">
        {loading
          ? "searching for"
          : `${totalResults} result${totalResults === 1 ? "" : "s"} for`} {" "}
        {/* Display active filter label */}
        {searchParams.has("search")
          ? `"${searchParams.get("search")}"`
          : searchParams.has("age")
          ? `age ${searchParams.get("age")} years`
          : searchParams.has("country")
          ? `vineyards from ${searchParams.get("country")}`
          : `vineyards with ${searchParams.get("variety")}`
        }
      </span>

      {/* Search input and button */}
      <Box display="flex" mt="20px">
        <input
          type="text"
          placeholder="Enter a vineyard name, winery name, region, grape variety, etc"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyUp={(e) => {
            if (e.key === "Enter" && searchText) runTextQuery();
          }}
          className="input-text search-input"
        />
        <button
          className="button u-ml-small search-button"
          disabled={!searchText}
          onClick={runTextQuery}
        >
          <span className="button-text">Search again</span>
        </button>
      </Box>

      {/* Display search options and tips if no results */}
      {tableData.length === 0 && !loading && (
        <>
          <div className="or-label">or</div>
                    <SearchOptions
            searchCountry={(country) => {
              setSearchText("");
              const params = { search: country, sort: sortByField, order: sortOrder, page: "1" };
              setSearchParams(params);
              loadData({ country }, sortByField, sortOrder, 0);
            }}
            searchVariety={(variety) => {
              setSearchText("");
              const params = { search: variety, sort: sortByField, order: sortOrder, page: "1" };
              setSearchParams(params);
              loadData({ variety }, sortByField, sortOrder, 0);
            }}
            searchAgeRange={(ageRange) => {
              setSearchText("");
              const params = { search: ageRange, sort: sortByField, order: sortOrder, page: "1" };
              setSearchParams(params);
              loadData({ age: ageRange }, sortByField, sortOrder, 0);
            }}
          />
          <SearchTips className="u-mt-large" />
        </>
      )}

      {/* Loading spinner */}
      <Loading loading={loading} />

      {/* Results table */}
      {tableData.length > 0 && (
        <>
          <Table hover size="sm" className="vineyard-table input-text u-mt-large">
            <thead>
              <tr>
                <TableHeader field="name" sortOrder={sortOrder} sortByField={sortByField} onOrderChange={reloadAfterSortOrderChange}>Vineyard name</TableHeader>
                <TableHeader field="country" sortOrder={sortOrder} sortByField={sortByField} onOrderChange={reloadAfterSortOrderChange}>Country</TableHeader>
                { bp === "desktop" && (
                  <>
                    <TableHeader field="region" sortOrder={sortOrder} sortByField={sortByField} onOrderChange={reloadAfterSortOrderChange}>Region</TableHeader>
                    <TableHeader field="subRegion" sortOrder={sortOrder} sortByField={sortByField} onOrderChange={reloadAfterSortOrderChange}>Subregion</TableHeader>
                    <TableHeader field="subSubRegion" sortOrder={sortOrder} sortByField={sortByField} onOrderChange={reloadAfterSortOrderChange}>Sub-Subregion</TableHeader>
                    <TableHeader field="yearPlanted" sortOrder={sortOrder} sortByField={sortByField} onOrderChange={reloadAfterSortOrderChange}>Date planted</TableHeader>
                  </>
                )}
                <TableHeader field="ageOfVines" sortOrder={sortOrder} sortByField={sortByField} onOrderChange={reloadAfterSortOrderChange}>Age</TableHeader>
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, i) => (
                <tr key={i}>
                  <TD>
                    <a
                      href={`${Paths.vineyard.replace(':id', row.composite)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        if (e.metaKey || e.ctrlKey) return;
                        e.preventDefault();
                        navigate(`${Paths.vineyard.replace(':id', row.composite)}`);
                      }}
                    >
                      <span className="search-link">{row.name}</span>
                    </a>
                  </TD>
                  <TD>{row.country}</TD>
                  { bp === "desktop" && 
                    <>
                      <TD>{row.region}</TD>
                      <TD>{row.subRegion}</TD>
                      <TD>{row.subSubRegion}</TD>
                      <TD>{row.yearPlanted}</TD>
                    </>
                  }
                  <TD>{row.ageOfVines}</TD>
                </tr>
              ))}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <Box display="flex" alignItems="center" mt="20px">
              <span className="description-text">Page {currentPage + 1} of {totalPages}</span>
              <Pagination className="u-ml-medium">
                <Pagination.First onClick={() => firstPage(sortByField, sortOrder)} disabled={currentPage === 0} />
                <Pagination.Prev onClick={() => prevPage(sortByField, sortOrder)} disabled={currentPage === 0} />
                <Pagination.Next onClick={() => nextPage(sortByField, sortOrder)} disabled={currentPage === totalPages - 1} />
                <Pagination.Last onClick={() => lastPage(sortByField, sortOrder)} disabled={currentPage === totalPages - 1} />
              </Pagination>
            </Box>
          )}
        </>
      )}
    </div>
    </>
  );
};